import {parseInteger} from '@benchling/js-utils';

/**
 * Value system representing data that might be stored in bulk assembly tables.
 *
 * This is designed as a self-contained system that's similar in spirit but much simpler than the TypedValue
 * system defined by TypedValueTypes.ts and typedvalueschema.json.
 *
 * To keep things concise while also not pollluting the global autocomplete namespace, all exported names are
 * prefixed with "BA" for BulkAssembly (though this is intentionally *not* present in the JSON).
 */

export enum BulkAssemblyOrientation {
  FORWARD = 'FORWARD',
  REVERSE = 'REVERSE',
}

// Note that order matters for this definition; menus are shown in this order.
export const BULK_ASSEMBLY_ORIENTATION_NAME: {[orientation in BulkAssemblyOrientation]: string} = {
  FORWARD: 'Forward',
  REVERSE: 'Reverse',
};

const ORIENTATION_BY_LOWER_NAME: Map<string, BulkAssemblyOrientation> = new Map(
  Object.entries(BULK_ASSEMBLY_ORIENTATION_NAME).map(
    ([orientation, name]: [BulkAssemblyOrientation, string]) => [name.toLowerCase(), orientation]
  )
);

export enum BAValueType {
  RAW_TEXT = 'RAW_TEXT',
  ID = 'ID',
  NUMERICAL_ID = 'NUMERICAL_ID',
  NUMBER = 'NUMBER',
  ORIENTATION = 'ORIENTATION',
}

export interface BARawTextValue {
  type: BAValueType.RAW_TEXT;
  rawText: string;
}

export interface BAIdValue {
  type: BAValueType.ID;
  id: string;
}

export interface BANumericIdValue {
  type: BAValueType.NUMERICAL_ID;
  id: number;
}

export interface BANumberValue {
  type: BAValueType.NUMBER;
  value: number;
}
export interface BAOrientationValue {
  type: BAValueType.ORIENTATION;
  value: BulkAssemblyOrientation;
}

type BAValue = BARawTextValue | BAIdValue | BANumericIdValue | BANumberValue | BAOrientationValue;

export function baNumber(value: number): BANumberValue {
  return {type: BAValueType.NUMBER, value};
}

export function baRawText(rawText: string): BARawTextValue {
  return {type: BAValueType.RAW_TEXT, rawText};
}

export function baId(id: string): BAIdValue {
  return {type: BAValueType.ID, id};
}

export function baNumericId(id: number): BANumericIdValue {
  return {type: BAValueType.NUMERICAL_ID, id};
}

export function baOrientation(orientation: BulkAssemblyOrientation): BAOrientationValue {
  return {type: BAValueType.ORIENTATION, value: orientation};
}

export const BA_EMPTY_VALUE = baRawText('');

export function isEmptyBAValue(value: BAValue): boolean {
  return value.type === BAValueType.RAW_TEXT && value.rawText === '';
}

/**
 * Format a start or end value to string if possible.
 *
 * Requires additional context about how to transform the value from "model" format to "display" format
 * since this differs between the start and end.
 */
export function formatBAEndpoint(
  value: BANumberValue | BARawTextValue,
  modelToDisplay: (value: number) => number
): string {
  if (value.type === BAValueType.NUMBER) {
    return String(modelToDisplay(value.value));
  } else {
    return value.rawText;
  }
}

/**
 * For a "number or raw text" representing a sequence index, convert to a numeric model value if possible.
 *
 * Requires additional context about how to transform the value from "display" format to "model" format
 * since this differs between the start and end.
 */
export function parseBAEndpoint(
  value: BANumberValue | BARawTextValue,
  displayToModel: (value: number) => number
): BANumberValue | BARawTextValue {
  if (value.type === BAValueType.NUMBER) {
    return value;
  }
  const text = value.rawText;
  const displayNumber = parseInteger(text);
  if (displayNumber == null) {
    return value;
  }
  const modelValue = displayToModel(displayNumber);
  return baNumber(modelValue);
}

export function parseBAOrientation(
  value: BAOrientationValue | BARawTextValue
): BAOrientationValue | BARawTextValue {
  if (isBAOrientation(value)) {
    return value;
  } else if (isEmptyBAValue(value)) {
    return baOrientation(BulkAssemblyOrientation.FORWARD);
  }
  const text = value.rawText;
  const resolvedOrientation = ORIENTATION_BY_LOWER_NAME.get(text.toLowerCase());
  return resolvedOrientation ? baOrientation(resolvedOrientation) : baRawText(text);
}

export function getOrientationWithFallback(
  value: BAOrientationValue | BARawTextValue
): BulkAssemblyOrientation {
  const parsedOrientation = parseBAOrientation(value);
  if (parsedOrientation.type === BAValueType.ORIENTATION) {
    return parsedOrientation.value;
  } else {
    return BulkAssemblyOrientation.FORWARD;
  }
}

export function isBARawText(tuple: BAValue): tuple is BARawTextValue {
  return tuple.type === BAValueType.RAW_TEXT;
}

export function isBAId(tuple: BAValue): tuple is BAIdValue {
  return tuple.type === BAValueType.ID;
}

export function isBANumericId(tuple: BAValue): tuple is BANumericIdValue {
  return tuple.type === BAValueType.NUMERICAL_ID;
}

export function isBANumber(tuple: BAValue): tuple is BANumberValue {
  return tuple.type === BAValueType.NUMBER;
}

export function isBAOrientation(tuple: BAValue): tuple is BAOrientationValue {
  return tuple.type === BAValueType.ORIENTATION;
}
